import {Form, InputNumber, Switch} from 'antd';
import {groupThousands, parseThousands} from '../../v2/numberFormat';
import v2 from '../../v2/instrumentV2.module.scss';
import {
  FormCard,
  spans,
  Hint,
  derivedClass,
  DerivedTag,
} from '../../v2/FormCard';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {TRADING_MOCK} from '../../v2/devPrefill';
import {useRemainingFields} from '../../v2/useRemainingFields';
import {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import moment from 'moment';
import {FeeSchedule} from 'components/feeSchedule/FeeSchedule';

export const BaseTrading = ({onSubmit, existingValues}) => {
  const [form] = Form.useForm();
  const {assetId} = useParams();

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  // Only *Units are writable — the gateway dropped min/maxTradeableAmount from
  // the instrument inputs. The amounts are resolved from the unit price, so
  // they are shown read-only rather than collected.
  const minUnits = Form.useWatch('minTradeableUnits', form);
  const maxUnits = Form.useWatch('maxTradeableUnits', form);
  const unitPrice =
    existingValues?.pricing?.unitPrice ??
    existingValues?.currentNetAssetValue ??
    existingValues?.currentMarketPrice;
  const [remaining, onValuesChange] = useRemainingFields(
    form,
    ['minTradeableUnits', 'maxTradeableUnits', 'orderExpirationLimit'],
    existingValues,
  );

  // On read the exchange is an object; on the create draft it is still the raw
  // id the picker wrote, which carries no code to name.
  const exchangeCode =
    typeof existingValues?.exchange === 'string'
      ? undefined
      : existingValues?.exchange?.code;

  const derivedAmount = units =>
    Number.isFinite(units) && Number.isFinite(unitPrice)
      ? units * unitPrice
      : undefined;

  useEffect(() => {
    if (existingValues) {
      const {startDate, endDate, feeSchedule, ...restValues} = existingValues;
      form.setFieldsValue({
        ...restValues,
        startDate: startDate ? moment(startDate, 'YYYY-MM-DD HH:mm:ss') : null,
        endDate: endDate ? moment(endDate, 'YYYY-MM-DD HH:mm:ss') : null,
        forDabaPro: existingValues?.forDabaPro,
        enabledForFreeSimulation: existingValues?.enabledForFreeSimulation,
        // The editor keeps fixed/variable amounts flat; the API nests them
        // under vector[].magnitude.
        feeSchedule: feeSchedule?.map(fees => ({
          ...fees,
          vector: fees?.vector?.map(vect => ({
            duration: vect?.duration,
            feeType: vect?.magnitude?.feeType,
            ...(vect?.magnitude?.feeType === 'FIXED' && {
              fixedAmount: vect?.magnitude?.value,
            }),
            ...(vect?.magnitude?.feeType === 'VARIABLE' && {
              variableAmount: vect?.magnitude?.value,
            }),
          })),
        })),
      });
    }
  }, [existingValues]);

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Trading settings</h2>
        <p>
          How this instrument behaves on the order book. Amounts are resolved
          from the unit price — edit the units and the amounts follow.
        </p>
      </div>

      <Form
        onFinish={values => {
          // `availableUnits` is rendered read-only but antd still submits
          // disabled fields, and it exists on no instrument input — the
          // gateway rejects the whole update if it is included.
          // eslint-disable-next-line no-unused-vars
          const {feeSchedule, availableUnits, ...restOfFields} = values;
          onSubmit({
            ...restOfFields,
            ...(feeSchedule?.length && {
              feeSchedule: feeSchedule.map(data => ({
                feeType: data?.feeType,
                feeName: data?.feeName,
                ...(data?.fixedFeeAmount && {
                  fixedFeeAmount: data?.fixedFeeAmount,
                }),
                ...(data?.variableFeeAmount && {
                  variableFeeAmount: data?.variableFeeAmount,
                }),
                ...(data?.feeType === 'ENTRY' && {
                  level: data?.level,
                  // Sent explicitly, not spread conditionally: the whole schedule is
                  // overwritten on save, so an omitted false reads as "unchanged"
                  // against a previously-true value and the toggle could never be
                  // turned back off.
                  perExchange: Boolean(data?.perExchange),
                }),
                scalable: data?.scalable ?? false,
                ...(data?.feeType === 'RECURRING' && {
                  frequency: data?.frequency,
                  origination: data?.origination,
                }),
                ...(data?.feeType === 'EXIT' &&
                  data?.vector?.length && {
                    vector: data.vector.map(vect => ({
                      duration: vect?.duration,
                      magnitude: {
                        feeType: vect?.feeType,
                        value:
                          vect?.feeType === 'FIXED'
                            ? vect?.fixedAmount
                            : vect?.variableAmount,
                      },
                    })),
                  }),
              })),
            }),
          });
        }}
        layout="vertical"
        form={form}
        onValuesChange={onValuesChange}>
        <FormCard
          title="Availability"
          description="Whether the instrument can be traded, and by whom">
          <div className={spans.third}>
            <Form.Item
              name="tradeable"
              label="Tradeable"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item
              name="fractionalShares"
              label="Fractional shares"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item
              name="forDabaPro"
              label="Daba Pro only"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item
              tooltip="Makes the instrument available in the free simulation portfolio"
              name="enabledForFreeSimulation"
              label="Enabled for free simulation"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item
              tooltip="Allows trades to draw from available inventory during auto-fill"
              name="useInventoryForTrade"
              label="Use inventory for trade"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Trade size"
          description="Configured in units. The gateway resolves the equivalent amounts.">
          <div className={spans.half}>
            <Form.Item name="minTradeableUnits" label="Min tradeable units">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="maxTradeableUnits" label="Max tradeable units">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={`${spans.half} ${derivedClass}`}>
            <Form.Item
              label={
                <>
                  Min tradeable amount <DerivedTag />
                </>
              }>
              <InputNumber
                disabled
                value={derivedAmount(minUnits)}
                formatter={groupThousands}
              />
            </Form.Item>
            <Hint>
              {unitPrice
                ? `units × unit price (${unitPrice})`
                : 'units × unit price — set a price to resolve'}
            </Hint>
          </div>
          <div className={`${spans.half} ${derivedClass}`}>
            <Form.Item
              label={
                <>
                  Max tradeable amount <DerivedTag />
                </>
              }>
              <InputNumber
                disabled
                value={derivedAmount(maxUnits)}
                formatter={groupThousands}
              />
            </Form.Item>
            <Hint>
              {unitPrice
                ? `units × unit price (${unitPrice})`
                : 'units × unit price — set a price to resolve'}
            </Hint>
          </div>
          <div className={spans.half}>
            <Form.Item name="orderExpirationLimit" label="Order expiry (days)">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          {assetId === 'FIXED_INCOMES' && (
            <>
              <div className={spans.half}>
                <Form.Item
                  name="allocatedUnits"
                  label="Total units allocated to this bond">
                  <InputNumber
                    formatter={groupThousands}
                    parser={parseThousands}
                  />
                </Form.Item>
              </div>
              <div className={`${spans.half} ${derivedClass}`}>
                <Form.Item
                  name="availableUnits"
                  tooltip="Units left for trading in this bond"
                  label={
                    <>
                      Available units left <DerivedTag />
                    </>
                  }>
                  <InputNumber disabled formatter={groupThousands} />
                </Form.Item>
              </div>
            </>
          )}
        </FormCard>

        {/* Only ever the instrument's own fees. This editor used to open with
            a copy of the exchange's schedule, which turned inheritance into a
            snapshot: an instrument meant to follow its venue got those amounts
            written onto its own record the first time anyone saved the step,
            and later changes at the exchange no longer reached it. Left empty,
            the instrument charges whatever its exchange charges. */}
        <FormCard
          title="Fee schedule"
          description={
            exchangeCode
              ? `Fees charged on trades in this instrument. Leave empty and it charges ${exchangeCode}'s fees for this instrument type.`
              : "Entry, exit and recurring fees applied to trades on this instrument. Leave empty and it charges the exchange's fees for this instrument type."
          }>
          <div className={spans.full}>
            <FeeSchedule feeName="feeSchedule" form={form} />
          </div>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => {
            form.setFieldsValue(TRADING_MOCK);
            onValuesChange();
          }}
          remaining={remaining}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
    </div>
  );
};
